<template>
  <div class="test-case-list">
    <a-page-header title="测试用例管理" sub-title="基于快照的测试用例管理">
      <template #extra>
        <a-button type="primary" @click="showCreateDialog">
          <template #icon><PlusOutlined /></template>
          创建测试用例
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <!-- 筛选区域 -->
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="分类">
          <a-select v-model:value="filters.category" style="width: 150px" allow-clear placeholder="全部">
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="故障诊断">故障诊断</a-select-option>
            <a-select-option value="知识查询">知识查询</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="优先级">
          <a-select v-model:value="filters.priority" style="width: 120px" allow-clear placeholder="全部">
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="high">高</a-select-option>
            <a-select-option value="medium">中</a-select-option>
            <a-select-option value="low">低</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select v-model:value="filters.status" style="width: 120px">
            <a-select-option value="active">活跃</a-select-option>
            <a-select-option value="archived">已归档</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="用例范围">
          <a-select v-model:value="filters.case_scope" style="width: 120px" allow-clear placeholder="全部">
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="step">单步</a-select-option>
            <a-select-option value="plan">整条计划</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="fetchTestCases">查询</a-button>
          <a-button style="margin-left: 8px" @click="resetFilters">重置</a-button>
        </a-form-item>
      </a-form>

      <!-- 统计信息 -->
      <a-row :gutter="16" style="margin-bottom: 16px" v-if="statistics">
        <a-col :span="6">
          <a-statistic title="总用例数" :value="statistics.total" />
        </a-col>
        <a-col :span="6">
          <a-statistic title="活跃用例" :value="statistics.active" />
        </a-col>
        <a-col :span="6">
          <a-statistic title="总执行次数" :value="statistics.total_executions" />
        </a-col>
        <a-col :span="6">
          <a-statistic title="平均分数" :value="statistics.overall_avg_score" :precision="2" />
        </a-col>
      </a-row>

      <!-- 表格 -->
      <a-table
        :columns="columns"
        :data-source="testCases"
        :loading="loading"
        :pagination="pagination"
        row-key="id"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'case_code'">
            <a-tag color="blue">{{ record.case_code }}</a-tag>
          </template>
          <template v-else-if="column.key === 'priority'">
            <a-tag :color="getPriorityColor(record.priority)">
              {{ getPriorityText(record.priority) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'status'">
            <a-tag :color="record.status === 'active' ? 'green' : 'default'">
              {{ record.status === 'active' ? '活跃' : '已归档' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'case_scope'">
            <a-tag :color="record.case_scope === 'plan' ? 'purple' : 'blue'">
              {{ record.case_scope === 'plan' ? (record.steps_count ? `整条(${record.steps_count}步)` : '整条计划') : '单步' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'avg_score'">
            <span v-if="record.avg_score">{{ record.avg_score.toFixed(2) }}</span>
            <span v-else>-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="viewDetail(record.id)">详情</a-button>
              <a-button type="link" size="small" @click="manageSnapshots(record.id)">快照</a-button>
              <a-button type="link" size="small" @click="editTestCase(record)">编辑</a-button>
              <a-popconfirm
                title="确定要删除此测试用例吗？"
                @confirm="deleteCase(record.id)"
              >
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 创建/编辑对话框 -->
    <a-modal
      v-model:open="createDialogVisible"
      :title="editingCase ? '编辑测试用例' : '创建测试用例'"
      width="900px"
      @ok="handleSave"
      @cancel="handleCancel"
      :okButtonProps="{ disabled: !canSave }"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 19 }"
      >
        <!-- 来源类型选择（仅创建时显示） -->
        <a-form-item label="来源类型" v-if="!editingCase">
          <a-radio-group v-model:value="formData.source_type" @change="handleSourceTypeChange">
            <a-radio value="execution_log">
              <span>基于执行记录创建</span>
              <a-tag color="green" style="margin-left: 8px">推荐</a-tag>
            </a-radio>
            <a-radio value="manual">手动创建</a-radio>
          </a-radio-group>
        </a-form-item>

        <!-- 执行记录选择器（基于执行记录时显示） -->
        <a-form-item 
          label="选择执行记录" 
          required 
          v-if="formData.source_type === 'execution_log' && !editingCase"
        >
          <a-select
            v-model:value="formData.execution_log_id"
            show-search
            :filter-option="false"
            placeholder="搜索步骤名称..."
            :not-found-content="executionLogSearching ? '搜索中...' : '无匹配结果'"
            @search="handleExecutionLogSearch"
            @change="handleExecutionLogSelect"
            style="width: 100%"
          >
            <a-select-option 
              v-for="log in executionLogs" 
              :key="log.id" 
              :value="log.id"
              :disabled="log.has_test_case"
            >
              <div class="execution-log-option">
                <span>{{ log.display_label }}</span>
                <a-tag v-if="log.has_test_case" color="orange" size="small">已创建</a-tag>
              </div>
            </a-select-option>
          </a-select>
          <div class="hint-text">输入步骤名称进行搜索，将显示最近10条匹配的执行记录</div>
        </a-form-item>

        <!-- 选中的执行记录预览 -->
        <a-form-item label="执行记录信息" v-if="selectedExecutionLog">
          <a-descriptions :column="2" bordered size="small">
            <a-descriptions-item label="步骤名称">{{ selectedExecutionLog.step_name }}</a-descriptions-item>
            <a-descriptions-item label="步骤顺序">步骤{{ selectedExecutionLog.step_order }}</a-descriptions-item>
            <a-descriptions-item label="故障现象" :span="2">{{ selectedExecutionLog.fault_phenomenon || '-' }}</a-descriptions-item>
            <a-descriptions-item label="控制单元">{{ selectedExecutionLog.control_unit || '-' }}</a-descriptions-item>
            <a-descriptions-item label="执行时间">{{ formatTime(selectedExecutionLog.create_time) }}</a-descriptions-item>
          </a-descriptions>
        </a-form-item>

        <a-divider />

        <a-form-item label="用例名称" required>
          <a-input 
            v-model:value="formData.case_name" 
            placeholder="请输入用例名称（基于执行记录时会自动生成）" 
          />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="formData.description" :rows="2" />
        </a-form-item>
        <a-form-item label="分类">
          <a-input v-model:value="formData.category" placeholder="如：故障诊断" />
        </a-form-item>
        <a-form-item label="优先级">
          <a-select v-model:value="formData.priority">
            <a-select-option value="high">高</a-select-option>
            <a-select-option value="medium">中</a-select-option>
            <a-select-option value="low">低</a-select-option>
          </a-select>
        </a-form-item>
        
        <a-divider>评分标准</a-divider>

        <!-- 评分模板选择 -->
        <a-form-item label="评分标准来源">
          <a-radio-group v-model:value="formData.criteria_source" @change="handleCriteriaSourceChange">
            <a-radio value="template">选择模板</a-radio>
            <a-radio value="custom">自定义</a-radio>
          </a-radio-group>
        </a-form-item>

        <!-- 模板选择器 -->
        <a-form-item label="选择评分模板" v-if="formData.criteria_source === 'template'">
          <a-select
            v-model:value="formData.template_id"
            placeholder="请选择评分模板"
            @change="handleTemplateSelect"
            style="width: 100%"
          >
            <!-- 按评估目标分组 -->
            <a-select-opt-group v-for="group in groupedTemplates" :key="group.target" :label="group.label">
              <a-select-option 
                v-for="template in group.templates" 
                :key="template.id" 
                :value="template.id"
              >
                <div class="template-option">
                  <span>{{ template.template_name }}</span>
                  <a-tag v-if="template.is_default" color="blue" size="small">默认</a-tag>
                </div>
              </a-select-option>
            </a-select-opt-group>
          </a-select>
        </a-form-item>
        
        <a-form-item label="A级标准（优秀）" required>
          <a-textarea
            v-model:value="formData.criteria_a"
            placeholder="完全符合预期，准确率>95%"
            :rows="2"
          />
        </a-form-item>
        <a-form-item label="B级标准（良好）" required>
          <a-textarea
            v-model:value="formData.criteria_b"
            placeholder="基本符合预期，准确率>80%"
            :rows="2"
          />
        </a-form-item>
        <a-form-item label="C级标准（及格）" required>
          <a-textarea
            v-model:value="formData.criteria_c"
            placeholder="部分符合预期，准确率>60%"
            :rows="2"
          />
        </a-form-item>
        <a-form-item label="通过阈值">
          <a-select v-model:value="formData.pass_threshold">
            <a-select-option value="A">A级及以上</a-select-option>
            <a-select-option value="B">B级及以上</a-select-option>
            <a-select-option value="C">C级及以上</a-select-option>
          </a-select>
        </a-form-item>

        <!-- 保存为新模板 -->
        <a-form-item label=" " :colon="false" v-if="formData.criteria_source === 'custom'">
          <a-checkbox v-model:checked="formData.save_as_template">保存为新评分模板</a-checkbox>
          <a-input 
            v-if="formData.save_as_template"
            v-model:value="formData.new_template_name"
            placeholder="请输入模板名称"
            style="width: 300px; margin-left: 16px"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 快照管理对话框 -->
    <a-modal
      v-model:open="snapshotDialogVisible"
      title="快照管理"
      width="1000px"
      :footer="null"
    >
      <snapshot-manager v-if="currentTestCaseId" :test-case-id="currentTestCaseId" />
    </a-modal>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons-vue';
import { debounce } from 'lodash-es';

const router = useRouter();
const route = useRoute();
import {
  getTestCases,
  getTestCaseStatistics,
  createTestCase,
  createTestCaseFromExecutionLog,
  updateTestCase,
  deleteTestCase,
  searchExecutionLogs
} from '@/api/evalTestCaseV2';
import { 
  getScoreTemplates, 
  getDefaultTemplate, 
  createScoreTemplate 
} from '@/api/evalScoreTemplate';
import SnapshotManager from './components/SnapshotManager.vue';

// 数据
const testCases = ref([]);
const loading = ref(false);
const statistics = ref(null);

// 执行记录搜索
const executionLogs = ref([]);
const executionLogSearching = ref(false);
const selectedExecutionLog = ref(null);

// 评分模板
const scoreTemplates = ref([]);

// 评估目标标签映射
const evalTargetLabels = {
  consistency: '遵从性评估',
  model_comparison: '模型对比评估',
  knowledge_structure: '知识结构评估',
  knowledge_content: '知识内容评估'
};

// 按评估目标分组的模板
const groupedTemplates = computed(() => {
  const groups = {};
  
  scoreTemplates.value.forEach(template => {
    const target = template.eval_target || 'general';
    if (!groups[target]) {
      groups[target] = {
        target,
        label: evalTargetLabels[target] || '通用模板',
        templates: []
      };
    }
    groups[target].templates.push(template);
  });
  
  // 按顺序返回：通用模板 -> 遵从性 -> 模型对比 -> 知识结构 -> 知识内容
  const order = ['general', 'consistency', 'model_comparison', 'knowledge_structure', 'knowledge_content'];
  return order.filter(t => groups[t]).map(t => groups[t]);
});

// 筛选
const filters = reactive({
  category: '',
  priority: '',
  status: 'active',
  case_scope: ''
});

// 分页
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

// 对话框
const createDialogVisible = ref(false);
const snapshotDialogVisible = ref(false);
const editingCase = ref(null);
const currentTestCaseId = ref(null);

// 表单
const formData = reactive({
  source_type: 'execution_log', // 默认基于执行记录
  execution_log_id: null,
  case_name: '',
  description: '',
  category: '',
  priority: 'medium',
  criteria_source: 'template', // template 或 custom
  template_id: null,
  criteria_a: '',
  criteria_b: '',
  criteria_c: '',
  pass_threshold: 'B',
  save_as_template: false,
  new_template_name: ''
});

// 是否可以保存
const canSave = computed(() => {
  // 基于执行记录时必须选择执行记录
  if (formData.source_type === 'execution_log' && !editingCase.value) {
    if (!formData.execution_log_id) return false;
  }
  // 必须有评分标准
  if (!formData.criteria_a || !formData.criteria_b || !formData.criteria_c) return false;
  return true;
});

// 表格列
const columns = [
  { title: '用例编码', dataIndex: 'case_code', key: 'case_code', width: 150 },
  { title: '用例名称', dataIndex: 'case_name', key: 'case_name', width: 200, ellipsis: true },
  { title: '范围', dataIndex: 'case_scope', key: 'case_scope', width: 100 },
  { title: '分类', dataIndex: 'category', key: 'category', width: 120 },
  { title: '优先级', dataIndex: 'priority', key: 'priority', width: 100 },
  { title: '状态', dataIndex: 'status', key: 'status', width: 100 },
  { title: '执行次数', dataIndex: 'execution_count', key: 'execution_count', width: 100 },
  { title: '平均分数', dataIndex: 'avg_score', key: 'avg_score', width: 100 },
  { title: '操作', key: 'action', width: 280, fixed: 'right' }
];

// 搜索执行记录（防抖）
const handleExecutionLogSearch = debounce(async (keyword) => {
  if (!keyword || keyword.length < 1) {
    // 没有关键词时加载最近10条
    await loadRecentExecutionLogs();
    return;
  }
  
  executionLogSearching.value = true;
  try {
    const res = await searchExecutionLogs(keyword, 10);
    if (res.code === 200) {
      executionLogs.value = res.data || [];
    }
  } catch (error) {
    console.error('搜索执行记录失败:', error);
  } finally {
    executionLogSearching.value = false;
  }
}, 300);

// 加载最近的执行记录
const loadRecentExecutionLogs = async () => {
  executionLogSearching.value = true;
  try {
    const res = await searchExecutionLogs('', 10);
    if (res.code === 200) {
      executionLogs.value = res.data || [];
    }
  } catch (error) {
    console.error('加载执行记录失败:', error);
  } finally {
    executionLogSearching.value = false;
  }
};

// 选择执行记录
const handleExecutionLogSelect = (logId) => {
  const log = executionLogs.value.find(l => l.id === logId);
  selectedExecutionLog.value = log;
  
  if (log) {
    // 自动填充用例名称
    const nameParts = [];
    if (log.step_order) nameParts.push(`[步骤${log.step_order}]`);
    if (log.fault_phenomenon) {
      const phenomenon = log.fault_phenomenon.length > 20 
        ? log.fault_phenomenon.substring(0, 20) + '...'
        : log.fault_phenomenon;
      nameParts.push(phenomenon);
    }
    if (log.control_unit) {
      nameParts.push(`- ${log.control_unit}`);
    }
    formData.case_name = nameParts.join(' ') || log.step_name;
    
    // 自动填充分类
    formData.category = '自动创建';
  }
};

// 来源类型切换
const handleSourceTypeChange = () => {
  formData.execution_log_id = null;
  selectedExecutionLog.value = null;
  formData.case_name = '';
};

// 评分标准来源切换
const handleCriteriaSourceChange = async () => {
  if (formData.criteria_source === 'template') {
    // 加载默认模板
    if (scoreTemplates.value.length > 0) {
      const defaultTemplate = scoreTemplates.value.find(t => t.is_default);
      if (defaultTemplate) {
        formData.template_id = defaultTemplate.id;
        applyTemplate(defaultTemplate);
      }
    }
  }
};

// 选择模板
const handleTemplateSelect = (templateId) => {
  const template = scoreTemplates.value.find(t => t.id === templateId);
  if (template) {
    applyTemplate(template);
  }
};

// 应用模板
const applyTemplate = (template) => {
  formData.criteria_a = template.criteria_a;
  formData.criteria_b = template.criteria_b;
  formData.criteria_c = template.criteria_c;
  formData.pass_threshold = template.pass_threshold || 'B';
};

// 加载评分模板
const loadScoreTemplates = async () => {
  try {
    const res = await getScoreTemplates({ is_active: true });
    if (res.code === 200) {
      scoreTemplates.value = res.data?.list || [];
      
      // 设置默认模板
      const defaultTemplate = scoreTemplates.value.find(t => t.is_default);
      if (defaultTemplate && !formData.template_id) {
        formData.template_id = defaultTemplate.id;
        applyTemplate(defaultTemplate);
      }
    }
  } catch (error) {
    console.error('加载评分模板失败:', error);
  }
};

// 方法
const fetchTestCases = async () => {
  loading.value = true;
  try {
    const res = await getTestCases({
      ...filters,
      page: pagination.current,
      pageSize: pagination.pageSize
    });
    if (res.code === 200 && res.data) {
      testCases.value = res.data.list || [];
      pagination.total = res.data.pagination?.total || 0;
    }
  } catch (error) {
    message.error('获取测试用例失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const fetchStatistics = async () => {
  try {
    const res = await getTestCaseStatistics();
    if (res.code === 200 && res.data) {
      statistics.value = res.data.overview || res.data;
    }
  } catch (error) {
    console.error('获取统计信息失败:', error);
  }
};

const handleTableChange = (pag) => {
  pagination.current = pag.current;
  pagination.pageSize = pag.pageSize;
  fetchTestCases();
};

const resetFilters = () => {
  filters.category = '';
  filters.priority = '';
  filters.status = 'active';
  filters.case_scope = '';
  pagination.current = 1;
  fetchTestCases();
};

const showCreateDialog = () => {
  editingCase.value = null;
  resetForm();
  loadRecentExecutionLogs();
  loadScoreTemplates();
  createDialogVisible.value = true;
};

const editTestCase = (record) => {
  editingCase.value = record;
  Object.assign(formData, {
    source_type: 'manual',
    case_name: record.case_name,
    description: record.description,
    category: record.category,
    priority: record.priority,
    criteria_source: 'custom',
    // 需要从详情接口获取完整数据
  });
  loadScoreTemplates();
  createDialogVisible.value = true;
};

const handleSave = async () => {
  try {
    // 如果选择保存为新模板
    if (formData.save_as_template && formData.new_template_name) {
      await createScoreTemplate({
        template_name: formData.new_template_name,
        criteria_a: formData.criteria_a,
        criteria_b: formData.criteria_b,
        criteria_c: formData.criteria_c,
        pass_threshold: formData.pass_threshold,
        created_by: 'admin'
      });
    }

    if (editingCase.value) {
      // 更新
      const data = {
        case_name: formData.case_name,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        pass_criteria: {
          criteria_a: formData.criteria_a,
          criteria_b: formData.criteria_b,
          criteria_c: formData.criteria_c,
          pass_threshold: formData.pass_threshold,
          auto_judge: true
        }
      };
      await updateTestCase(editingCase.value.id, data);
      message.success('更新成功');
    } else {
      // 创建
      if (formData.source_type === 'execution_log') {
        // 基于执行记录创建
        const res = await createTestCaseFromExecutionLog(formData.execution_log_id, {
          case_name: formData.case_name || undefined, // 不传让后端自动生成
          description: formData.description,
          category: formData.category,
          priority: formData.priority,
          criteria_a: formData.criteria_a,
          criteria_b: formData.criteria_b,
          criteria_c: formData.criteria_c,
          pass_threshold: formData.pass_threshold,
          create_snapshot: true
        });
        
        if (res.code === 200 || res.code === 201) {
          message.success('测试用例创建成功，已自动创建基线快照');
        } else {
          message.error(res.message || '创建失败');
          return;
        }
      } else {
        // 手动创建
        const data = {
          case_name: formData.case_name,
          description: formData.description,
          category: formData.category,
          priority: formData.priority,
          criteria_a: formData.criteria_a,
          criteria_b: formData.criteria_b,
          criteria_c: formData.criteria_c,
          pass_threshold: formData.pass_threshold,
          create_snapshot: true
        };
        await createTestCase(data);
        message.success('创建成功');
      }
    }
    
    createDialogVisible.value = false;
    fetchTestCases();
    fetchStatistics();
  } catch (error) {
    message.error('保存失败：' + error.message);
  }
};

const handleCancel = () => {
  createDialogVisible.value = false;
  resetForm();
};

const resetForm = () => {
  Object.assign(formData, {
    source_type: 'execution_log',
    execution_log_id: null,
    case_name: '',
    description: '',
    category: '',
    priority: 'medium',
    criteria_source: 'template',
    template_id: null,
    criteria_a: '',
    criteria_b: '',
    criteria_c: '',
    pass_threshold: 'B',
    save_as_template: false,
    new_template_name: ''
  });
  selectedExecutionLog.value = null;
};

const viewDetail = (id) => {
  router.push(`/eval-v2/test-case/${id}`);
};

const manageSnapshots = (id) => {
  currentTestCaseId.value = id;
  snapshotDialogVisible.value = true;
};

const deleteCase = async (id) => {
  try {
    await deleteTestCase(id);
    message.success('删除成功');
    fetchTestCases();
    fetchStatistics();
  } catch (error) {
    message.error('删除失败：' + error.message);
  }
};

const showImportDialog = () => {
  message.info('批量导入功能开发中...');
};

const getPriorityColor = (priority) => {
  const colors = {
    high: 'red',
    medium: 'orange',
    low: 'green'
  };
  return colors[priority] || 'default';
};

const getPriorityText = (priority) => {
  const texts = {
    high: '高',
    medium: '中',
    low: '低'
  };
  return texts[priority] || priority;
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  const time = new Date(timeStr);
  return time.toLocaleString('zh-CN');
};

// 初始化
onMounted(async () => {
  fetchTestCases();
  fetchStatistics();
  
  // 检查 URL 参数，如果有 action=create，自动打开创建对话框
  if (route.query.action === 'create') {
    showCreateDialog();
    
    // 如果有 execution_log_id 参数，预填执行日志
    if (route.query.execution_log_id) {
      formData.source_type = 'execution_log';
      const logId = parseInt(route.query.execution_log_id);
      
      // 通过 ID 精确查找该执行日志
      try {
        const res = await searchExecutionLogs('', 10, logId);
        if (res.code === 200 && res.data && res.data.length > 0) {
          const targetLog = res.data[0];
          executionLogs.value = [targetLog];
          formData.execution_log_id = logId;
          handleExecutionLogSelect(logId);
        } else {
          message.warning('未找到指定的执行记录，请手动搜索选择');
        }
      } catch (error) {
        console.error('加载执行记录失败:', error);
        message.error('加载执行记录失败');
      }
      
      // 清除 URL 参数，避免刷新后重复触发
      router.replace({ path: route.path });
    }
  }
});
</script>

<style scoped>
.test-case-list {
  padding: 24px;
}

.execution-log-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.template-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hint-text {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
</style>
